import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCustomerJourney } from '../../../context/setup';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faMercadoPago } from '@/static/icons/faMercadoPago';
import classNames from 'classnames';
import { Row } from '@/components';
import { faBank } from '@fortawesome/free-solid-svg-icons';
import { faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import { Button } from '@/components/Button/ButtonV2';
import { ActionType, PaymentMethod } from '../../../context/types';
import { useState, useEffect } from 'react';
import { StepperActionsProps } from '../../CustomerJourneyStepper';
import { useAuth } from '@/contexts';
import { DEFAULT_INSTRUCTIONS } from '../../../context/utils';
import { motion } from 'framer-motion';
import { Check, Trash2 } from 'lucide-react';
import { PaymentInstructions } from './PaymentInstructions';
import { OfflinePaymentTypeE, SupportedCountriesE, WalletTypeE } from '@/types/cyclone/models';
import { GetConnectI } from '@/types/cyclone/requests';
import { useClient } from '@/hooks';
import { useQuery } from 'react-query';
import { trackGenericEvent } from '@/analytics';

interface PaymentMethodProps {
  title: string;
  value: string;
  icon: string | IconDefinition;
  isEnabled: boolean;
  features: string[];
}

const PaymentMethodCard: React.FC<
  PaymentMethodProps & {
    onSelect: (method: string) => void;
    onRemove?: (method: string) => void;
    onDisconnectMP?: () => void;
  }
> = ({ title, icon, isEnabled, features, value, onSelect, onRemove, onDisconnectMP }) => {
  const { state } = useCustomerJourney();
  const { client } = useClient();
  const { session } = useAuth();

  const handleSwitchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(value);
  };

  const { data: connectMP } = useQuery(
    ['mercado_pago', 'connect'],
    async () =>
      await client<GetConnectI>('wallet/connect', 'POST', {
        isAuthRequired: true,
        data: {
          wallet_type: WalletTypeE.MERCADO_PAGO,
          email: session?.email
        }
      }),
    {
      retry: false,
      refetchOnWindowFocus: false
    }
  );

  const handleConnectMP = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Track Mercado Pago connection attempt
    trackGenericEvent('Journey Payment Method MercadoPago Connect Clicked');

    window.location.href = connectMP?.url || '';
  };

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      onClick={() => (title === 'Mercado Pago' ? handleConnectMP : onSelect(value))}
      className={classNames(
        'relative flex flex-col sm:flex-row sm:items-center sm:justify-between border p-4 rounded-xl w-full max-w-3xl cursor-pointer bg-white mx-auto hover:shadow-lg transition-all',
        isEnabled ? 'border-blue bg-blue/5' : 'border-gray-200'
      )}
    >
      <div
        className={classNames('absolute -top-[1px] -left-[1px] h-[calc(100%+2px)] w-[10px] rounded-l-xl', {
          'bg-gray-300': !isEnabled,
          'bg-blue': isEnabled
        })}
      />

      <div className="flex flex-col w-full sm:flex-row sm:items-center">
        <div className="flex justify-between items-center w-full sm:w-auto">
          <div className="flex items-center">
            {/* Icono */}
            <div className="text-2xl sm:text-3xl mr-3 text-blue flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue/10 shrink-0">
              <FontAwesomeIcon
                icon={icon as IconDefinition}
                fixedWidth
                size={icon === faMercadoPago ? 'lg' : '1x'}
                color={isEnabled ? '#0072FB' : '#212121'}
              />
            </div>

            <h2 className="font-medium text-base sm:text-lg sm:hidden line-clamp-1">{title}</h2>
          </div>

          <div className="flex sm:hidden gap-2 items-center">
            {title === 'Mercado Pago' ? (
              state.paymentMethods.includes(PaymentMethod.CARD) ? (
                <div className="flex gap-2">
                  <div className="flex text-sm items-center gap-2 px-3 py-2 bg-blue/10 border-blue border rounded-lg text-blue font-medium">
                    <Check size={14} />
                    <span>Conectado</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDisconnectMP?.();
                    }}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={handleConnectMP}>
                  Conectar
                </Button>
              )
            ) : isEnabled ? (
              <>
                <div className="px-3 py-1.5 bg-blue/10 border-blue border rounded-lg text-blue font-medium text-sm">
                  Editar
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove?.(value);
                  }}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={handleSwitchClick}>
                Habilitar
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 mt-3 sm:mt-0 border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-100">
          <h2 className="font-medium text-lg mb-1.5 hidden sm:block">{title}</h2>
          <div className="space-y-1">
            {features.map((feature, index) => (
              <Row key={index} gap={2} className="text-sm text-gray-600 items-start">
                <div className="text-blue mb-1 flex-shrink-0">•</div>
                <span className="text-[13px] sm:text-sm leading-tight">{feature}</span>
              </Row>
            ))}
          </div>
        </div>
      </div>

      <div className="hidden sm:flex sm:mt-0 sm:ml-6 sm:pl-4 sm:border-l sm:border-gray-200 sm:items-center sm:justify-end sm:gap-2">
        {title === 'Mercado Pago' ? (
          state.paymentMethods.includes(PaymentMethod.CARD) ? (
            <div className="flex gap-2">
              <div className="flex text-sm items-center gap-2 px-3 py-2 bg-blue/10 border-blue border rounded-lg text-blue font-medium">
                <Check size={14} />
                <span>Conectado</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDisconnectMP?.();
                }}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={handleConnectMP}>
              Conectar
            </Button>
          )
        ) : isEnabled ? (
          <>
            <div className="px-3 py-2 bg-blue/10 border-blue border rounded-lg text-blue font-medium text-sm">
              Editar
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(value);
              }}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 size={16} />
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={handleSwitchClick}>
            Habilitar
          </Button>
        )}
      </div>
    </motion.article>
  );
};

interface PaymentMethodsStepProps {
  setStepActions: (actions: StepperActionsProps) => void;
}

export const PaymentMethodsStep: React.FC<PaymentMethodsStepProps> = ({ setStepActions }) => {
  const { state, dispatch, mutationResetWallet } = useCustomerJourney();
  const { session } = useAuth();

  const country = session?.vendor?.country || 'AR';

  const paymentGateways = session?.vendor?.payment_gateways || [];
  const isMercadoPagoAvailable = country !== SupportedCountriesE.CHILE;
  const isWalletAssociated = paymentGateways.includes(WalletTypeE.MERCADO_PAGO);
  const isCashEnabled = session?.vendor?.is_payment_cash_enabled;
  const isBankTransferEnabled = session?.vendor?.is_payment_bank_transfer_enabled;

  useEffect(() => {
    // Solo inicializar desde el backend si no hay estado previo en localStorage
    const hasExistingPaymentMethods = state.paymentMethods.length > 0;
    const hasExistingInstructions = state.cashInstructions || state.transferInstructions;

    if (!hasExistingPaymentMethods && !hasExistingInstructions) {
      // Primera carga o reinicio - inicializar desde el backend
      if (isMercadoPagoAvailable && isWalletAssociated) {
        dispatch({
          type: ActionType.TOGGLE_PAYMENT_METHOD,
          payload: PaymentMethod.CARD
        });
      }
      if (isBankTransferEnabled) {
        dispatch({
          type: ActionType.TOGGLE_PAYMENT_METHOD,
          payload: PaymentMethod.TRANSFER
        });
        dispatch({
          type: ActionType.SET_TRANSFER_INSTRUCTIONS,
          payload:
            session.vendor?.offline_payments?.find(
              (payment) => payment.type === OfflinePaymentTypeE.BANK_TRANSFER
            )?.description || ''
        });
      }
      if (isCashEnabled) {
        dispatch({
          type: ActionType.TOGGLE_PAYMENT_METHOD,
          payload: PaymentMethod.CASH
        });
        dispatch({
          type: ActionType.SET_CASH_INSTRUCTIONS,
          payload:
            session.vendor?.offline_payments?.find((payment) => payment.type === OfflinePaymentTypeE.CASH)
              ?.description || ''
        });
      }
    }
  }, [
    session,
    dispatch,
    isMercadoPagoAvailable,
    isWalletAssociated,
    isBankTransferEnabled,
    isCashEnabled,
    state.paymentMethods.length,
    state.cashInstructions,
    state.transferInstructions
  ]);

  const [showInstructions, setShowInstructions] = useState({ show: false, value: '' });
  const [isInstructionsModified, setIsInstructionsModified] = useState(false);

  const handleTogglePaymentMethod = (method: string) => {
    // Si el método ya está habilitado (excepto Mercado Pago), abrir para editar
    if (method !== PaymentMethod.CARD && state.paymentMethods.includes(method as PaymentMethod)) {
      setShowInstructions({ show: true, value: method });
      setIsInstructionsModified(false);

      // Track payment method edit
      trackGenericEvent('Journey Payment Method Edit Started', {
        method
      });
    } else if (method === PaymentMethod.CARD) {
      // Mercado Pago toggle normal
      dispatch({
        type: ActionType.TOGGLE_PAYMENT_METHOD,
        payload: method as PaymentMethod
      });

      // Track payment method toggle
      trackGenericEvent('Journey Payment Method Toggled', {
        method,
        enabled: !state.paymentMethods.includes(method as PaymentMethod)
      });
    } else {
      // Método no habilitado, abrir para configurar
      setShowInstructions({ show: true, value: method });
      setIsInstructionsModified(false);

      // Track payment method instruction setup start
      trackGenericEvent('Journey Payment Method Instructions Started', {
        method
      });
    }
  };

  const handleRemovePaymentMethod = (method: string) => {
    dispatch({
      type: ActionType.TOGGLE_PAYMENT_METHOD,
      payload: method as PaymentMethod
    });

    // Track payment method removal
    trackGenericEvent('Journey Payment Method Removed', {
      method
    });
  };

  const handleDisconnectMercadoPago = () => {
    if (!session?.vendor?.id) return;
    trackGenericEvent('Journey Payment Method MercadoPago Disconnect Clicked');
    dispatch({
      type: ActionType.TOGGLE_PAYMENT_METHOD,
      payload: PaymentMethod.CARD
    });

    mutationResetWallet.mutate(session.vendor);
  };

  useEffect(() => {
    if (showInstructions.show) {
      const isTransfer = showInstructions.value === PaymentMethod.TRANSFER;
      const defaultText =
        DEFAULT_INSTRUCTIONS[showInstructions.value as keyof typeof DEFAULT_INSTRUCTIONS]?.[
          country as keyof typeof DEFAULT_INSTRUCTIONS[PaymentMethod.TRANSFER]
        ];
      const currentText = isTransfer ? state.transferInstructions : state.cashInstructions;

      setStepActions({
        showSaveCancel: true,
        canSave: isInstructionsModified,
        onCancel: () => {
          setShowInstructions({ show: false, value: '' });
          setIsInstructionsModified(false);
        },
        onSave: () => {
          // Solo hacer toggle si el método no está habilitado (configuración inicial)
          const isMethodEnabled = state.paymentMethods.includes(showInstructions.value as PaymentMethod);

          if (!isMethodEnabled) {
            dispatch({
              type: ActionType.TOGGLE_PAYMENT_METHOD,
              payload: showInstructions.value as PaymentMethod
            });

            // Track payment method enabled after instructions
            trackGenericEvent('Journey Payment Method Enabled', {
              method: showInstructions.value,
              instructionsModified: isInstructionsModified
            });
          } else {
            // Track payment method edited
            trackGenericEvent('Journey Payment Method Instructions Updated', {
              method: showInstructions.value,
              instructionsModified: isInstructionsModified
            });
          }

          setShowInstructions({ show: false, value: '' });
          setIsInstructionsModified(false);
        }
      });

      // Verificar si las instrucciones han sido modificadas
      setIsInstructionsModified(currentText !== '' && currentText !== defaultText);
    } else {
      setStepActions({});
    }
  }, [
    showInstructions,
    state.transferInstructions,
    state.cashInstructions,
    state.paymentMethods,
    setStepActions,
    isInstructionsModified,
    country,
    dispatch
  ]);

  const handleInstructionsChange = (html: string) => {
    const isTransfer = showInstructions.value === PaymentMethod.TRANSFER;
    const defaultText =
      showInstructions.value in DEFAULT_INSTRUCTIONS
        ? DEFAULT_INSTRUCTIONS[showInstructions.value as keyof typeof DEFAULT_INSTRUCTIONS]?.[
            country as keyof typeof DEFAULT_INSTRUCTIONS[PaymentMethod.TRANSFER]
          ]
        : '';
    const actionType = isTransfer ? ActionType.SET_TRANSFER_INSTRUCTIONS : ActionType.SET_CASH_INSTRUCTIONS;

    dispatch({ type: actionType, payload: html });
    setIsInstructionsModified(html !== '' && html !== defaultText);
  };

  const paymentMethods = [
    {
      title: 'Mercado Pago',
      value: PaymentMethod.CARD,
      icon: faMercadoPago,
      isEnabled: state.paymentMethods.includes(PaymentMethod.CARD),
      features: [
        'Pagos con tarjetas o saldo en Mercado Pago',
        'Fondos anticipados en tu billetera',
        'Comisiones por procesamiento'
      ]
    },
    {
      title: 'Transferencias',
      value: PaymentMethod.TRANSFER,
      icon: faBank,
      isEnabled: state.paymentMethods.includes(PaymentMethod.TRANSFER),
      features: [
        'Pagos manuales con transferencia bancaria',
        'Billeteras virtuales compatibles',
        'Sin comisión'
      ]
    },
    {
      title: 'Pagos en efectivo',
      value: PaymentMethod.CASH,
      icon: faMoneyBill1,
      isEnabled: state.paymentMethods.includes(PaymentMethod.CASH),
      features: ['Pagos manuales en efectivo', 'Ideal para clientes habituales', 'Sin comisión']
    }
  ].filter(Boolean) as PaymentMethodProps[];

  const filteredPaymentMethods = paymentMethods.filter((method) => {
    if (!isMercadoPagoAvailable) return method.value !== PaymentMethod.CARD;
    else return paymentMethods;
  });

  return (
    <div className="space-y-6">
      {showInstructions.show ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <PaymentInstructions
            paymentMethod={showInstructions.value as PaymentMethod}
            onInstructionsChange={handleInstructionsChange}
          />
        </motion.div>
      ) : (
        <>
          <div className="text-center mb-6">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="text-2xl font-bold text-gray-900"
            >
              ¿Cómo quieres cobrar?
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="text-gray-600 mt-2"
            >
              Habilita al menos un método para cobrar a tus clientes
            </motion.p>
          </div>

          <div className="flex flex-col gap-5">
            {filteredPaymentMethods.map((method, index) => (
              <PaymentMethodCard
                key={index}
                {...method}
                onSelect={handleTogglePaymentMethod}
                onRemove={handleRemovePaymentMethod}
                onDisconnectMP={method.value === PaymentMethod.CARD ? handleDisconnectMercadoPago : undefined}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
